import { useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { PublicLayout } from './components/layout/PublicLayout';
import { AdminLayout } from './components/layout/AdminLayout';
import { ProtectedRoute } from './routes/ProtectedRoute';
import { usePageTracking } from './hooks/usePageTracking';

// Public pages
import { SpecialsPage } from './features/specials/pages/SpecialsPage';
import { EventsPage } from './features/events/pages/EventsPage';
import { HappyHoursPage } from './features/happy-hours/pages/HappyHoursPage';
import { BarsPage } from './features/bars/pages/BarsPage';
import { BarDetailPage } from './features/bars/pages/BarDetailPage';
import { FavoritesPage } from './features/favorites/pages/FavoritesPage';
import { SearchPage } from './features/search';
import { LocationPage } from './features/locations/pages/LocationPage';
import { SettingsPage, HiddenBarsPage, AccountPage } from './features/settings';
import { HelpPage } from './features/help/pages/HelpPage';
import { FeedbackPage } from './features/feedback';
import { PrivacyPage } from './features/privacy';
import { TermsPage } from './features/terms';
import { GuidePage, GuidesIndexPage } from './features/guides';

// Auth pages
import { LoginPage } from './features/auth/pages/LoginPage';
import { AuthCallbackPage } from './features/auth/pages/AuthCallbackPage';
import { MobileCallbackPage } from './features/auth/pages/MobileCallbackPage';

// Admin pages
import { DashboardPage } from './features/admin/pages/DashboardPage';
import { BarManagePage } from './features/admin/pages/BarManagePage';
import { SpecialsManagePage } from './features/admin/pages/SpecialsManagePage';
import { EventsManagePage } from './features/admin/pages/EventsManagePage';
import { ClaimRequestsPage } from './features/admin/pages/ClaimRequestsPage';
import { MetrosPage } from './features/admin/pages/MetrosPage';
import { ScriptsPage } from './features/admin/pages/ScriptsPage';
import { LocationsPage } from './features/admin/pages/LocationsPage';
import { DiscoveryLogPage } from './features/admin/pages/DiscoveryLogPage';
import { AnalyticsPage } from './features/admin/pages/AnalyticsPage';

export default function App() {
  // Track page views with location context
  usePageTracking();

  return (
    <Routes>
      {/* Public routes */}
      <Route element={<PublicLayout />}>
        <Route index element={<SpecialsPage />} />
        <Route path="specials" element={<SpecialsPage />} />
        <Route path="events" element={<EventsPage />} />
        <Route path="happy-hours" element={<HappyHoursPage />} />
        <Route path="bars" element={<BarsPage />} />
        <Route path="bars/:id" element={<BarDetailPage />} />
        <Route path="favorites" element={<FavoritesPage />} />
        <Route path="search" element={<SearchPage />} />

        {/* Guide articles */}
        <Route path="guides" element={<GuidesIndexPage />} />
        <Route path="guide/:slug" element={<GuidePage />} />

        {/* City/Location landing pages */}
        <Route path="city/:slug" element={<LocationPage />}>
          <Route index element={<SpecialsPage />} />
          <Route path="specials" element={<SpecialsPage />} />
          <Route path="events" element={<EventsPage />} />
          <Route path="happy-hours" element={<HappyHoursPage />} />
          <Route path="bars" element={<BarsPage />} />
        </Route>
      </Route>

      {/* App download pages */}
      <Route path="download" element={<DownloadRedirect />} />
      <Route path="app" element={<AppDownloadPage />} />

      {/* Auth routes */}
      <Route path="login" element={<LoginPage />} />
      <Route path="auth/callback" element={<AuthCallbackPage />} />
      <Route path="auth/mobile-callback" element={<MobileCallbackPage />} />

      {/* Settings & Help */}
      <Route path="settings" element={<SettingsPage />} />
      <Route path="settings/account" element={<AccountPage />} />
      <Route path="settings/hidden" element={<HiddenBarsPage />} />
      <Route path="help" element={<HelpPage />} />
      <Route path="feedback" element={<FeedbackPage />} />
      <Route path="privacy" element={<PrivacyPage />} />
      <Route path="terms" element={<TermsPage />} />

      {/* Admin routes */}
      <Route
        path="admin"
        element={
          <ProtectedRoute requireBarAccess>
            <AdminLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<DashboardPage />} />
        <Route path="bars/:id" element={<BarManagePage />} />
        <Route path="bars/:id/specials" element={<SpecialsManagePage />} />
        <Route path="bars/:id/events" element={<EventsManagePage />} />
        <Route
          path="claim-requests"
          element={
            <ProtectedRoute requireAdmin>
              <ClaimRequestsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="metros"
          element={
            <ProtectedRoute requireAdmin>
              <MetrosPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="scripts"
          element={
            <ProtectedRoute requireAdmin>
              <ScriptsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="locations"
          element={
            <ProtectedRoute requireAdmin>
              <LocationsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="discovery-log"
          element={
            <ProtectedRoute requireAdmin>
              <DiscoveryLogPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="analytics"
          element={
            <ProtectedRoute requireAdmin>
              <AnalyticsPage />
            </ProtectedRoute>
          }
        />
      </Route>

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/specials" replace />} />
    </Routes>
  );
}

const APP_STORE_URL = 'https://apps.apple.com/us/app/bar-guide-specials-and-more/id6758872064';
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.mouserlabs.barguide&hl=en_US';

function DownloadRedirect() {
  const ua = navigator.userAgent;
  const isIOS = /iphone|ipad|ipod/i.test(ua);
  const isAndroid = /android/i.test(ua);

  useEffect(() => {
    if (isIOS) {
      window.location.replace(APP_STORE_URL);
    } else if (isAndroid) {
      window.location.replace(PLAY_STORE_URL);
    } else {
      window.location.replace('/');
    }
  }, []);

  // Fallback UI for browsers that block auto-redirect (e.g. Brave)
  if (!isIOS && !isAndroid) return null;

  const storeUrl = isIOS ? APP_STORE_URL : PLAY_STORE_URL;
  const storeName = isIOS ? 'App Store' : 'Google Play';

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#09090b', color: '#fafafa', fontFamily: 'Inter, -apple-system, sans-serif' }}>
      <div style={{ textAlign: 'center', padding: '2rem' }}>
        <h1 style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: '0.5rem' }}>Get Bar Guide</h1>
        <p style={{ color: '#a1a1aa', fontSize: '0.875rem', marginBottom: '1.5rem' }}>Discover the best bar specials and events near you</p>
        <a
          href={storeUrl}
          style={{ display: 'inline-block', padding: '0.75rem 1.5rem', borderRadius: '0.5rem', background: '#f59e0b', color: '#09090b', fontWeight: 600, fontSize: '0.875rem', textDecoration: 'none' }}
        >
          Download on {storeName}
        </a>
      </div>
    </div>
  );
}

function AppDownloadPage() {
  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#09090b', color: '#fafafa', fontFamily: 'Inter, -apple-system, sans-serif' }}>
      <div style={{ textAlign: 'center', padding: '2rem', maxWidth: '480px' }}>
        <img src="/logo_glass.svg" alt="Bar Guide" style={{ width: '80px', height: '80px', marginBottom: '1.5rem', borderRadius: '1rem', display: 'block', marginLeft: 'auto', marginRight: 'auto' }} />
        <h1 style={{ fontSize: '2rem', fontWeight: 700, marginBottom: '0.5rem', lineHeight: 1.2 }}>Get the Bar Guide App</h1>
        <p style={{ color: '#a1a1aa', fontSize: '1rem', marginBottom: '2.5rem', lineHeight: 1.5 }}>
          Discover happy hours, specials & events at 4,700+ bars. Updated daily. Free.
        </p>
        <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap' }}>
          <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-block', transition: 'opacity 0.2s' }} onMouseEnter={e => (e.currentTarget.style.opacity = '0.8')} onMouseLeave={e => (e.currentTarget.style.opacity = '1')}>
            <img src="/app-store-badge.svg" alt="Download on the App Store" style={{ height: '48px' }} />
          </a>
          <a href={PLAY_STORE_URL} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-block', transition: 'opacity 0.2s' }} onMouseEnter={e => (e.currentTarget.style.opacity = '0.8')} onMouseLeave={e => (e.currentTarget.style.opacity = '1')}>
            <img src="/google-play-badge.png" alt="Get it on Google Play" style={{ height: '70px', margin: '-11px 0' }} />
          </a>
        </div>
        <p style={{ color: '#52525b', fontSize: '0.75rem', marginTop: '2rem' }}>Available on iOS and Android</p>
      </div>
    </div>
  );
}
